import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Avatar, ThemeToggle, NavIcon } from '../common/index.jsx';
import { attendanceAPI } from '../../api';
import toast from 'react-hot-toast';

const MobileNavContext = createContext({
  mobileOpen: false,
  setMobileOpen: () => {},
  toggleMobile: () => {},
  closeMobile: () => {},
});

export const useMobileNav = () => useContext(MobileNavContext);

const NAV = [
  { to:'/dashboard',    icon:'HomeIcon',                    label:'Dashboard'     },
  { to:'/tasks',        icon:'ClipboardDocumentListIcon',   label:'Tasks'          },
  { to:'/archive',      icon:'ArchiveBoxIcon',              label:'Archive'        },
  { to:'/departments',  icon:'BuildingOfficeIcon',          label:'Departments'    },
  { to:'/users',        icon:'UsersIcon',                   label:'Team'           },
  { to:'/groups',       icon:'UserGroupIcon',               label:'Groups'         },
  { to:'/notifications',icon:'BellIcon',                    label:'Notifications'  },
  { to:'/presence',     icon:'CheckCircleIcon',             label:'Presence'       },
  { to:'/my-report',    icon:'ChartBarIcon',                label:'My Report'      },
  { to:'/timesheet',    icon:'ClockIcon',                   label:'My Timesheet'   },
  { to:'/attendance',   icon:'FingerPrintIcon',             label:'My Attendance'  },
  { to:'/privacy-policy',icon:'ShieldCheckIcon',            label:'Privacy Policy' },
  { to:'/terms',        icon:'DocumentTextIcon',            label:'Terms & Conditions' },
  { to:'/refund-policy',icon:'ReceiptRefundIcon',           label:'Refund Policy'  },
  { to:'/contact-us',   icon:'PhoneIcon',                   label:'Contact Us'     },
];

const ADMIN_NAV = [
  { to:'/calendar',         icon:'CalendarDaysIcon',        label:'Team Calendar'      },
  { to:'/mis',              icon:'DocumentChartBarIcon',    label:'MIS Report'         },
  { to:'/admin-timesheet',  icon:'ClipboardDocumentListIcon', label:'Team Timesheets'  },
  { to:'/broadcasts',       icon:'MegaphoneIcon',           label:'Broadcast'          },
  { to:'/reopen-requests',  icon:'LockOpenIcon',            label:'Reopen Requests'    },
  { to:'/deadline-requests',icon:'ClockIcon',               label:'Deadline Requests'  },
  { to:'/settings',         icon:'Cog6ToothIcon',           label:'Settings'           },
  { to:'/team-performance', icon:'TrophyIcon',              label:'Team Performance'   },
  { to:'/assignment-history', icon:'ListBulletIcon',         label:'Assignment History' },
  { to:'/admin-attendance', icon:'CalendarDaysIcon',        label:'Attendance Report'  },
  { to:'/plans',            icon:'CreditCardIcon',          label:'Plans & Billing'    },
];

const SUPERADMIN_NAV = [
  { to:'/superadmin?tab=dashboard',   tabKey:'dashboard',   icon:'ChartBarIcon',        label:'Dashboard'       },
  { to:'/superadmin?tab=clients',     tabKey:'clients',     icon:'UsersIcon',           label:'Clients'         },
  { to:'/superadmin?tab=plans',       tabKey:'plans',       icon:'CreditCardIcon',      label:'Plans'           },
  { to:'/superadmin?tab=payments',    tabKey:'payments',    icon:'BuildingOffice2Icon', label:'Payments'        },
  { to:'/superadmin?tab=register',    tabKey:'register',    icon:'BuildingOfficeIcon',  label:'Register Client' },
];

export const Sidebar = () => {
  const { mobileOpen, closeMobile } = useMobileNav();
  const { user, logout, isAdmin, isSuperAdmin, isManager, theme, toggleTheme, unreadCount, hasFeature } = useAuth();

  const visibleNav = NAV.filter(item => {
    if (item.to === '/attendance' || item.to === '/presence') {
      return hasFeature ? hasFeature('attendanceTracking') : true;
    }
    return true;
  });

  const visibleAdminNav = ADMIN_NAV.filter(item => {
    if (item.to === '/mis' || item.to === '/team-performance') {
      return hasFeature ? hasFeature('misReports') : false;
    }
    if (item.to === '/admin-attendance') {
      return hasFeature ? hasFeature('attendanceTracking') : true;
    }
    return true;
  });
  const navigate = useNavigate();

  const base     = 'flex items-center gap-3 py-2.5 pr-3 text-sm font-medium transition-all duration-150 group relative';
  const active   = 'bg-brand-500/10 dark:bg-brand-500/[0.04] text-brand-600 dark:text-brand-400 border-l-[3px] border-brand-500 rounded-r-xl rounded-l-none pl-[11px] font-bold';
  const inactive = 'text-slate-650 dark:text-slate-400 hover:bg-slate-200/40 dark:hover:bg-slate-800/20 hover:text-slate-900 dark:hover:text-slate-200 border-l-[3px] border-transparent pl-[11px]';
  const tenantName = isSuperAdmin ? 'Super Admin' : (user?.tenantId?.name || 'Workspace');

  return (
    <>
      {/* Mobile Drawer Overlay Backdrop */}
      {mobileOpen && (
        <div 
          onClick={closeMobile}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 lg:hidden transition-opacity"
        />
      )}

      <aside className={`
        w-64 h-screen bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800/80 flex flex-col flex-shrink-0
        fixed top-0 bottom-0 left-0 z-50 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Workspace Header */}
        <div className="px-5 py-5 border-b border-slate-100/80 dark:border-slate-800/40 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-2xl flex items-center justify-center text-white font-extrabold text-sm flex-shrink-0 shadow-lg ${isSuperAdmin ? 'bg-gradient-to-br from-violet-500 to-purple-700' : 'bg-gradient-to-br from-brand-500 to-brand-700'}`}>
              {isSuperAdmin ? 'S' : tenantName[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-900 dark:text-white text-sm leading-tight truncate">{tenantName}</p>
              <p className="text-xs text-slate-400 leading-tight">{isSuperAdmin ? 'Platform Admin' : 'Workspace'}</p>
            </div>
          </div>
          <button onClick={closeMobile} className="lg:hidden p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {!isSuperAdmin && visibleNav.map(item => {
            const isNotification = item.to === '/notifications';
            return (
              <React.Fragment key={item.to}>
                <NavLink to={item.to} onClick={closeMobile} className={({ isActive }) => `${base} ${isActive ? active : inactive}`}>
                  <NavIcon name={item.icon} className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                  <span className="truncate flex-1">{item.label}</span>
                  {isNotification && unreadCount > 0 && (
                    <span className="bg-red-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 animate-pulse min-w-[18px] text-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </NavLink>
                {item.to === '/tasks' && isManager && (
                  <NavLink to="/reopen-requests" onClick={closeMobile} className={({ isActive }) => `${base} ${isActive ? active : inactive}`}>
                    <NavIcon name="LockOpenIcon" className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                    <span className="truncate flex-1">Reopen Requests</span>
                  </NavLink>
                )}
                {item.to === '/attendance' && isManager && (
                  <NavLink to="/admin-attendance?tab=leaves" onClick={closeMobile} className={({ isActive }) => `${base} ${isActive ? active : inactive}`}>
                    <NavIcon name="CalendarDaysIcon" className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                    <span className="truncate flex-1">Leave Requests</span>
                  </NavLink>
                )}
              </React.Fragment>
            );
          })}
          {isSuperAdmin && SUPERADMIN_NAV.map(item => {
            const currentTab = new URLSearchParams(window.location.search).get('tab') || 'clients';
            const isActive   = currentTab === item.tabKey || (!window.location.search && item.tabKey === 'clients');
            return (
              <Link key={item.to} to={item.to} onClick={closeMobile} className={`${base} ${isActive ? active : inactive}`}>
                <NavIcon name={item.icon} className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                <span className="truncate flex-1">{item.label}</span>
              </Link>
            );
          })}
          {isAdmin && !isSuperAdmin && (
            <>
              <div className="pt-4 pb-1 px-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Admin Tools</p>
              </div>
              {visibleAdminNav.map(item => (
                <NavLink key={item.to} to={item.to} onClick={closeMobile} className={({ isActive }) => `${base} ${isActive ? active : inactive}`}>
                  <NavIcon name={item.icon} className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Quick Attendance Widget & Profile Wrapper */}
        <div className="mt-auto border-t border-slate-200/60 dark:border-slate-800/40 pt-2 flex flex-col gap-1 flex-shrink-0">
          {!isSuperAdmin && user && (!hasFeature || hasFeature('attendanceTracking')) && (
            <SidebarAttendanceWidget />
          )}

          {/* User profile footer */}
          <div className="px-3 pb-3 pt-1">
            <div
              onClick={() => { closeMobile(); navigate('/settings'); }}
              className="flex items-center gap-3 p-2.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-850 shadow-sm transition-colors group cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
            >
              <Avatar name={user?.name} src={user?.avatar} size="sm"/>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 dark:text-white truncate leading-tight group-hover:text-indigo-500">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize leading-tight">{user?.role}</p>
              </div>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <ThemeToggle theme={theme} onToggle={toggleTheme}/>
                <button
                  onClick={() => { closeMobile(); logout(); navigate('/login'); }}
                  title="Logout"
                  className="p-1.5 rounded-lg text-slate-450 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const SidebarAttendanceWidget = () => {
  const [status, setStatus] = useState(null);
  const [acting, setActing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);

  const fetchStatus = async () => {
    try {
      const res = await attendanceAPI.getStatus();
      const att = res.data.data?.attendance || res.data.data;
      setStatus(att);
    } catch (e) {
      console.warn("Failed to fetch attendance status in sidebar:", e.message);
    }
  };

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(fetchStatus, 30000);
    window.addEventListener('attendance-update', fetchStatus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('attendance-update', fetchStatus);
    };
  }, []);

  useEffect(() => {
    const a = status;
    if (!a?.checkIn || a?.checkOut || !a?.isCheckedIn) {
      if (a?.activeMinutes) setElapsed(a.activeMinutes * 60);
      setBreakElapsed(0);
      return;
    }

    const updateElapsed = () => {
      const lastSession = a.sessions && a.sessions.length > 0 ? a.sessions[a.sessions.length - 1] : null;
      const currentSessionStart = lastSession ? new Date(lastSession.checkIn).getTime() : new Date(a.checkIn).getTime();
      
      const previousSessionMins = a.sessions && a.sessions.length > 1
        ? a.sessions.slice(0, -1).reduce((sum, s) => sum + (s.durationMinutes || 0), 0)
        : 0;

      if (a.isOnBreak) {
        const activeBreak = a.breaks && a.breaks.length > 0 ? a.breaks[a.breaks.length - 1] : null;
        const breakStart = activeBreak ? new Date(activeBreak.start).getTime() : Date.now();
        const currentSessionSecs = Math.floor((breakStart - currentSessionStart) / 1000);
        const totalWorkedSecs = (previousSessionMins * 60) + Math.max(0, currentSessionSecs);
        setElapsed(Math.max(0, totalWorkedSecs - (a.totalBreakMinutes || 0) * 60));
        setBreakElapsed(Math.max(0, Math.floor((Date.now() - breakStart) / 1000)));
      } else {
        const currentSessionSecs = Math.floor((Date.now() - currentSessionStart) / 1000);
        const totalWorkedSecs = (previousSessionMins * 60) + currentSessionSecs;
        setElapsed(Math.max(0, totalWorkedSecs - (a.totalBreakMinutes || 0) * 60));
        setBreakElapsed(0);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [status]);

  const handleAction = async (apiCall, successMsg) => {
    setActing(true);
    try {
      const res = await apiCall();
      toast.success(successMsg);
      if (res?.data?.data?.attendance) {
        setStatus(res.data.data.attendance);
      } else {
        await fetchStatus();
      }
      window.dispatchEvent(new CustomEvent('attendance-update'));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Attendance action failed');
    } finally {
      setActing(false);
    }
  };

  const formatElapsed = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  if (!status) return null;

  const isCheckedIn  = !!status.isCheckedIn || (!!status.checkIn && !status.checkOut);
  const isOnBreak    = isCheckedIn && !!status.isOnBreak;
  const isCheckedOut = !isCheckedIn && !!status.checkOut;

  let statusLabel = 'Not Checked In';
  let dotColor = 'bg-red-500';
  if (isOnBreak) {
    statusLabel = status.currentBreakType === 'lunch' ? 'Lunch Break' : 'Short Break';
    dotColor = 'bg-amber-500 animate-pulse';
  } else if (isCheckedIn) {
    statusLabel = 'Working';
    dotColor = 'bg-emerald-500';
  } else if (isCheckedOut) {
    statusLabel = 'Checked Out';
    dotColor = 'bg-slate-400';
  }

  return (
    <div className="px-3 py-1">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-2.5 border border-slate-200/80 dark:border-slate-850 shadow-sm">
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">
              {isOnBreak ? 'Break Time' : 'Attendance'}
            </span>
            {isCheckedIn && (
              <span className={`text-sm font-extrabold font-mono mt-0.5 block leading-none ${isOnBreak ? 'text-amber-500 animate-pulse' : 'text-slate-800 dark:text-white'}`}>
                {isOnBreak ? formatElapsed(breakElapsed) : formatElapsed(elapsed)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 self-start">
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[90px]">{statusLabel}</span>
          </div>
        </div>

        {/* Buttons */}
        {!isCheckedIn && (
          <button
            onClick={() => handleAction(attendanceAPI.checkIn, 'Checked in successfully!')}
            disabled={acting}
            className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm hover:shadow flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <span>Check In</span>
          </button>
        )}

        {isCheckedIn && !isOnBreak && (
          <div className="flex gap-1.5">
            <button
              onClick={() => handleAction(() => attendanceAPI.startBreak('short'), 'Short Break started')}
              disabled={acting}
              className="flex-1 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <span>Break</span>
            </button>
            <button
              onClick={() => handleAction(() => attendanceAPI.startBreak('lunch'), 'Lunch Break started')}
              disabled={acting}
              className="flex-1 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <span>Lunch</span>
            </button>
            <button
              onClick={() => handleAction(attendanceAPI.checkOut, 'Checked out successfully!')}
              disabled={acting}
              className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center disabled:opacity-50"
              title="Check Out"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7" />
              </svg>
            </button>
          </div>
        )}

        {isOnBreak && (
          <button
            onClick={() => handleAction(attendanceAPI.endBreak, 'Break ended')}
            disabled={acting}
            className="w-full py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <span>Resume Work</span>
          </button>
        )}
      </div>
    </div>
  );
};

export const Layout = ({ title, subtitle, actions, children }) => {
  const { toggleMobile } = useMobileNav();
  const navigate = useNavigate();
  const [planLocked, setPlanLocked] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [limitHit,   setLimitHit]   = useState(null);

  useEffect(() => {
    const handleLocked = (e) => {
      setPlanLocked(true);
      setLockReason(e.detail?.reason || 'locked');
    };
    const handleLimit = (e) => {
      setLimitHit(e.detail);
      setTimeout(() => setLimitHit(null), 8000);
    };
    window.addEventListener('plan-locked', handleLocked);
    window.addEventListener('plan-limit',  handleLimit);
    return () => {
      window.removeEventListener('plan-locked', handleLocked);
      window.removeEventListener('plan-limit',  handleLimit);
    };
  }, []);

  return (
    <div className="flex-1 min-w-0">

      {/* Plan locked banner */}
      {planLocked && (
        <div className="bg-red-600 text-white px-4 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <p className="font-bold text-sm">
                {lockReason === 'expired'
                  ? 'Your plan has expired — workspace is in read-only mode.'
                  : 'Your workspace is locked — read-only mode active.'}
              </p>
              <p className="text-xs text-red-200 mt-0.5">
                You can still view all data. All edits are blocked until you renew.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/plans')}
            className="bg-white text-red-600 font-bold text-sm px-4 py-2 rounded-lg
                       hover:bg-red-50 transition-colors whitespace-nowrap flex-shrink-0"
          >
            Upgrade Plan →
          </button>
        </div>
      )}

      {/* Limit hit banner */}
      {limitHit && (
        <div className="bg-amber-500 text-white px-4 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-bold text-sm">{limitHit.message}</p>
              <p className="text-xs text-amber-100 mt-0.5">
                {limitHit.current}/{limitHit.max} {limitHit.limitType} used on your current plan.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/plans')}
              className="bg-white text-amber-600 font-bold text-sm px-4 py-2 rounded-lg
                         hover:bg-amber-50 transition-colors whitespace-nowrap"
            >
              View Plans →
            </button>
            <button
              onClick={() => setLimitHit(null)}
              className="text-white hover:text-amber-200 transition-colors p-1"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="px-4 sm:px-6 lg:px-8 py-3.5 sm:py-5 border-b border-slate-100/80 dark:border-slate-800/40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg sticky top-0 z-20">
        <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile Hamburger Toggle Button */}
            <button 
              onClick={toggleMobile}
              className="lg:hidden p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
              title="Toggle Menu"
              aria-label="Toggle Navigation Menu"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white truncate">{title}</h1>
              {subtitle && <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 sm:gap-3 flex-wrap">{actions}</div>}
        </div>
      </div>

      <main className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">{children}</main>
    </div>
  );
};

export const AppLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobile = () => setMobileOpen(prev => !prev);
  const closeMobile = () => setMobileOpen(false);

  return (
    <MobileNavContext.Provider value={{ mobileOpen, setMobileOpen, toggleMobile, closeMobile }}>
      <div className="flex h-screen bg-slate-50 dark:bg-slate-950 premium-gradient-bg overflow-hidden relative">
        <Sidebar />
        <div className="flex-1 overflow-y-auto flex flex-col min-w-0">
          {children}
        </div>
      </div>
    </MobileNavContext.Provider>
  );
};

export const PageLayout = Layout;